import React, { useEffect, useState, Suspense, lazy, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuthenticatedFetch } from '../utils/api';
import styles from './ExcalidrawCanvas.module.css';

// Lazy load Excalidraw
const Excalidraw = lazy(() => import('@excalidraw/excalidraw').then(module => ({
  default: module.Excalidraw
})));

const ExcalidrawCanvas = () => {
  const [isClient, setIsClient] = useState(false);
  const { theme } = useTheme();
  const authenticatedFetch = useAuthenticatedFetch();
  const [excalidrawAPI, setExcalidrawAPI] = useState(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const saveDrawing = useCallback(async () => {
    if (!excalidrawAPI) return;
    
    const elements = excalidrawAPI.getSceneElements();
    const appState = excalidrawAPI.getAppState();
    
    try {
      const response = await authenticatedFetch('/api/drawings', {
        method: 'POST',
        body: JSON.stringify({
          elements,
          appState
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save drawing');
      }
      
      // You might want to show a success message here
      console.log('Drawing saved successfully');
    } catch (error) {
      console.error('Error saving drawing:', error);
      // You might want to show an error message to the user here
    }
  }, [excalidrawAPI, authenticatedFetch]);

  if (!isClient) {
    return <div>Loading drawing canvas...</div>;
  }

  return (
    <div className={styles.excalidrawWrapper}>
      <Suspense fallback={<div>Loading drawing canvas...</div>}>
        <Excalidraw 
          theme={{
            appearance: theme.background === '#FFFFFF' ? 'light' : 'dark'
          }}
          initialData={{
            appState: {
              currentItemStrokeWidth: 1,
              defaultLinearElement: {
                startArrowhead: null,
                endArrowhead: null
              },
              defaultFreeDrawOptions: {
                strokeWidth: 1,
                roughness: 0,
                simulatePressure: true,
                smoothing: 0.5
              }
            }
          }}
          UIOptions={{
            canvasActions: {
              toggleTheme: false,
              export: false,
              loadScene: false,
              saveScene: false,
              saveAsImage: false,
              clearCanvas: true,
              changeViewBackgroundColor: false,
            },
            dockedSidebarBreakpoint: 0,
            showToolbar: true,
            displayMenuButton: false,
            displayLibraryButton: false
          }}
          toolbarProps={{
            tools: [
              { id: "selection", name: "Selection" },
              { id: "rectangle", name: "Rectangle" },
              { id: "diamond", name: "Diamond" },
              { id: "ellipse", name: "Ellipse" },
              { id: "arrow", name: "Arrow" },
              { id: "line", name: "Line" },
              { id: "freedraw", name: "Pen" },
              { id: "text", name: "Text" },
              { id: "eraser", name: "Eraser" }
            ],
            customTools: [],
            mobileDefaultToolbarPosition: "bottom",
            defaultToolbarPosition: "top",
            toolbarItemPadding: 6,
            showToolbarToggles: false,
            toolbarSize: "medium"
          }}
          excalidrawAPI={setExcalidrawAPI}
        />
        <button 
          onClick={saveDrawing}
          className={styles.saveButton}
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            padding: '8px 16px',
            backgroundColor: '#1a73e8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Save Drawing
        </button>
      </Suspense>
    </div>
  );
};

export default ExcalidrawCanvas; 